import React, { FC } from 'react';
import { connect } from 'react-redux';
import * as tracking from 'dibs-tracking';
import { trackChatOpen } from './helpers/tracking';
import { SESSION_CHAT_STATE_KEY } from './constants';
import { localStorage } from 'dibs-browser-storage';
import { graphql, createFragmentContainer, Environment } from 'react-relay';
import { getAbTestTrackingData } from 'dibs-buyer-layout/exports/clientABTestHelpers';

// components
import { PdpRespDealerChatLazy } from './PdpRespDealerChatLazy';
import { BuyerChatLazy } from 'dibs-buyer-chat/exports/BuyerChatLazy';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// types
import { PdpRespDealerChatLoader_viewer as Viewer } from './__generated__/PdpRespDealerChatLoader_viewer.graphql';

type Props = {
    viewer: Viewer;
    isClient: boolean;
    environment: Environment;
    isInteractivePdpChat: boolean;
};

export const PdpRespDealerChatLoaderComponent: FC<Props> = ({
    viewer,
    isClient,
    environment,
    isInteractivePdpChat,
}) => {
    const { pdpChatFeatureFlag } = viewer;
    const chatVisibilityRef = React.useRef(null);
    const [isMinimized, setIsMinimized] = React.useState(false);
    const [isChatVisible, setIsChatVisible] = React.useState(false);
    const [shouldLoadChat, setShouldLoadChat] = React.useState(false);

    React.useEffect(() => {
        if (pdpChatFeatureFlag && isChatVisible) {
            const { isMinimized: isInitiallyMinimized = false } =
                (localStorage.getItem(SESSION_CHAT_STATE_KEY) as { isMinimized: boolean }) || {};
            trackChatOpen(isInitiallyMinimized);
        }
    }, [pdpChatFeatureFlag, isChatVisible]);

    React.useEffect(() => {
        const { isMinimized: isInitiallyMinimized = false } =
            (localStorage.getItem(SESSION_CHAT_STATE_KEY) as { isMinimized: boolean }) || {};
        setIsMinimized(isInitiallyMinimized);

        // AB Test tracking
        const trackingData = getAbTestTrackingData(document.cookie, 'PDP_CHAT_V2');
        if (trackingData?.testGroup) {
            tracking.trackAbTestEvent(trackingData, 'pdp_pageview');
        }
    }, []);

    React.useEffect(() => {
        setShouldLoadChat(!!(pdpChatFeatureFlag && isClient));
    }, [pdpChatFeatureFlag, isClient]);

    const handleVisibilityChange = React.useCallback(
        ({ isVisible, boundingClientRect, disconnect }) => {
            if (isVisible || boundingClientRect?.top < 0) {
                setIsChatVisible(true);
                disconnect();
            }
        },
        []
    );

    return (
        <>
            <div ref={chatVisibilityRef} />
            <VisibilityTracker
                elementRef={chatVisibilityRef}
                onVisibilityChange={handleVisibilityChange}
            />
            {shouldLoadChat &&
                isChatVisible &&
                (isInteractivePdpChat ? (
                    <BuyerChatLazy
                        viewer={viewer}
                        environment={environment}
                        isInitiallyMinimized={isMinimized}
                    />
                ) : (
                    <PdpRespDealerChatLazy
                        viewer={viewer}
                        environment={environment}
                        isInitiallyMinimized={isMinimized}
                    />
                ))}
        </>
    );
};

type ReduxStoreType = {
    relayVariables: {
        variables: {
            isClient: boolean;
            isInteractivePdpChat: boolean;
        };
    };
};

function mapStateToProps(
    state: ReduxStoreType
): { isClient: boolean; isInteractivePdpChat: boolean } {
    const {
        relayVariables: { variables },
    } = state;

    return {
        isClient: !!variables?.isClient,
        isInteractivePdpChat: !!variables?.isInteractivePdpChat,
    };
}

export const PdpRespDealerChatLoader = createFragmentContainer(
    connect(mapStateToProps)(PdpRespDealerChatLoaderComponent),
    {
        viewer: graphql`
            fragment PdpRespDealerChatLoader_viewer on Viewer {
                pdpChatFeatureFlag: isEnabled(key: "pdpChat")
                ...BuyerChatLazy_viewer @include(if: $isInteractivePdpChat)
                ...PdpRespDealerChatLazy_viewer @skip(if: $isInteractivePdpChat)
            }
        `,
    }
);
